import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FetchOptions } from "@danishagro/shared/src/interfaces/fetchOptions.interface";
import toast from "react-hot-toast";
import { DA_Toast } from "@danishagro/shared/src/components/molecules/Toast/Toast.component";
import { getRelativeRootOnLocalhost } from "@danishagro/shared/src/helpers/getRelativeUrlOnLocalhost.helper";
import { B2C_SITE_ORIGIN } from "@danishagro/shared/src/constants/urlRoot.constants";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { useScreenReader } from "@danishagro/shared/src/contexts/useScreenReader.context";
import { useRequest } from "@danishagro/shared/src/hooks/useRequest.hook";
import {
    BasketArgs,
    CompleteOrder,
    DA_BasketData,
    DA_BasketDataApi,
    DA_BasketOrderLine,
    DA_BasketPriceData,
} from "../../interfaces/basket.interface";
import { useTranslations } from "../translations/translations.context";
import { useBasketRequest } from "./hooks/useBasketRequest.hook";
import { mapToBasketData, mapToBasketLineApi, mapToOrder } from "./helpers/basket.helper";

interface BasketHook {
    basketId?: string;
    secret?: string;
    orderSecret?: string;
    priceData: DA_BasketPriceData | undefined;
    shippingDetails: DA_BasketData["shippingDetails"];
    deliveryMethod: DA_BasketData["deliveryMethod"];
    orderLines: DA_BasketOrderLine[];
    addToBasket: (properties: BasketArgs, options?: FetchOptions) => Promise<DA_BasketData>;
    updateBasket: (properties: BasketArgs, options?: FetchOptions) => Promise<DA_BasketData>;
    removeFromBasket: (properties: BasketArgs, options?: FetchOptions) => Promise<DA_BasketData>;
    patchDeliveryMethod: (deliveryMethod: string, options?: FetchOptions) => void;
    updateDeliveryMethod: (
        carrier: string,
        carrier_service: string,
        options?: FetchOptions
    ) => Promise<DA_BasketData>;
    resetBasket: () => void;
    getCompletedOrder: (
        orderId: string,
        orderSecret: string,
        options?: FetchOptions
    ) => Promise<CompleteOrder>;
    isLoadingBasket: boolean;
    isUpdatingBasket: boolean;
    isFirstAdd: boolean;
    messageOnUpdatingCart: string;
    updatingCartMessage: (msg: UpdateBasketType) => void;
}

const BasketContext = React.createContext<BasketHook>({} as BasketHook);

type Props = {
    orderSecret?: string;
    children: React.ReactNode;
};

interface UpdateBasketType {
    method: "add" | "delete" | "update";
}

export const BasketProvider = ({ orderSecret: givenOrderSecret, children }: Props): JSX.Element => {
    // Check if this is a B2B site (hatting-agro) to use simplified logic
    const isB2BSite = useMemo(() => {
        return (
            globalThis.siteSettingsObj?.siteHeader === "hatting-agro" ||
            globalThis.b2bsitevariant === "hatting-agro-b2b"
        );
    }, []);

    // Initialize basket state - only use localStorage for B2C sites
    const [basketId, setBasketId] = useState<string>(() => {
        const isSiteB2B =
            globalThis.siteSettingsObj?.siteHeader === "hatting-agro" ||
            globalThis.b2bsitevariant === "hatting-agro-b2b";
        if (!isSiteB2B && typeof window !== "undefined") {
            return localStorage.getItem("basketId") || undefined;
        }
        return undefined;
    });
    const [basketSecret, setBasketSecret] = useState<string>(() => {
        const isSiteB2B =
            globalThis.siteSettingsObj?.siteHeader === "hatting-agro" ||
            globalThis.b2bsitevariant === "hatting-agro-b2b";
        if (!isSiteB2B && typeof window !== "undefined") {
            return localStorage.getItem("basketSecret") || undefined;
        }
        return undefined;
    });
    const [orderSecret, _setOrderSecret] = useState<string>(givenOrderSecret);
    const [isLoadingBasket, setIsLoadingBasket] = useState(false);
    const [isUpdatingBasket, setIsUpdatingBasket] = useState(false);
    const [orderLines, setOrderLines] = useState<DA_BasketOrderLine[]>([]);
    const [messageOnUpdatingCart, setMessageOnUpdatingCart] = useState("");
    const { setScreenReaderMessage } = useScreenReader();

    const [isFirstAdd, setIsFirstAdd] = useState(false);

    const [shippingDetails, setShippingDetails] = useState<DA_BasketData["shippingDetails"]>();
    const [deliveryMethod, setDeliveryMethod] = useState<DA_BasketData["deliveryMethod"]>();
    const [priceData, setPriceData] = useState<DA_BasketPriceData>(undefined);

    const { getDictionaryString } = useTranslations();

    const { currentCulture, customerNumber, siteVariant } = useAppData();
    const b2cRequest = useBasketRequest();
    const request = useRequest();

    const isBasketMerged = useRef(false);

    const findUpdatedLine = (
        orderLines: DA_BasketOrderLine[] | undefined,
        properties: BasketArgs
    ): DA_BasketOrderLine | undefined => {
        if (!orderLines) return undefined;

        if (properties.id) {
            const byId = orderLines.find((line) => line.id === properties.id);
            if (byId) return byId;
        }
        if (properties.productId) {
            return [...orderLines]
                .reverse()
                .find((line) => line.productId === properties.productId);
        }
        return undefined;
    };

    const showBasketToast = ({
        productName,
        getDictionaryString,
    }: {
        productName?: string;
        getDictionaryString: (key: string) => string;
    }) => {
        toast.custom(() => (
            <DA_Toast
                href={`${getRelativeRootOnLocalhost(B2C_SITE_ORIGIN)}/cart`}
                goTo={getDictionaryString("ToastShowBasket")}
                message={
                    <>
                        <div>{getDictionaryString("ToastBasketUpdatedText")}</div>
                        <br />
                        <div>
                            <strong>{productName}</strong> <br />
                        </div>
                    </>
                }
            />
        ));
    };

    const loggedIn = useMemo(() => {
        return globalThis.isAuthenticated ?? false;
    }, []);

    const storeBasketSecret = useCallback(
        (secret: string) => {
            if (basketSecret !== secret) {
                // Only store in localStorage for B2C sites
                if (!isB2BSite) {
                    localStorage.setItem("basketSecret", secret);
                }
                setBasketSecret(secret);
            }
        },
        [basketSecret, isB2BSite]
    );

    const storeBasketId = useCallback(
        (id: string) => {
            if (basketId !== id) {
                // Only store in localStorage for B2C sites
                if (!isB2BSite) {
                    localStorage.setItem("basketId", id);
                }
                setBasketId(id);
            }
        },
        [basketId, isB2BSite]
    );

    const fetchAnonymousBasket = useCallback(
        (basketId: string, basketSecret: string): Promise<DA_BasketDataApi> => {
            // For hatting-agro-b2b, add customerNumber as query parameter
            const queryParams: Record<string, string> = {};

            if (
                (siteVariant?.alias === "hatting-agro-b2b" ||
                    globalThis?.b2bsitevariant === "hatting-agro-b2b") &&
                customerNumber
            ) {
                queryParams.customerNumber = customerNumber;
            }

            return b2cRequest
                .get(`/api/basket/${basketId}/`, basketSecret, queryParams)
                .then((response) => response.json());
            // .then((data) => new Promise((resolve) => setTimeout(() => resolve(data), 5000)));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [b2cRequest, siteVariant, customerNumber]
    );

    const [totalQuantity, setTotalQuantity] = useState(0);
    const [lastUpdateMethod, setLastUpdateMethod] = useState<UpdateBasketType | null>(null);

    useEffect(() => {
        const items = orderLines;
        const total = items?.reduce((sum, item) => sum + item?.quantity, 0);
        setTotalQuantity(total);
    }, [orderLines]);

    useEffect(() => {
        if (!lastUpdateMethod) return;
        if (lastUpdateMethod.method === "add") {
            setScreenReaderMessage(
                getDictionaryString("accessibility.screenReader.message.add", {
                    totalQuantity: totalQuantity,
                })
            );
        } else if (lastUpdateMethod.method === "delete") {
            setScreenReaderMessage(
                getDictionaryString("accessibility.screenReader.message.delete", {
                    totalQuantity: totalQuantity,
                })
            );
        } else if (lastUpdateMethod.method === "update") {
            setScreenReaderMessage(
                getDictionaryString("accessibility.screenReader.message.update", {
                    totalQuantity: totalQuantity,
                })
            );
        }
    }, [
        totalQuantity,
        lastUpdateMethod,
        setMessageOnUpdatingCart,
        setScreenReaderMessage,
        getDictionaryString,
    ]);

    const updatingCartMessage = useCallback((method: UpdateBasketType) => {
        setLastUpdateMethod(method);
    }, []);

    const mergeAnonymousBasket = useCallback(
        (basketId: string, basketSecret: string): Promise<DA_BasketDataApi> => {
            const queryParams = new URLSearchParams({
                culture: currentCulture,
            });

            return b2cRequest
                .patch(`/api/basket/${basketId}/merge/`, basketSecret, queryParams.toString())
                .then((response) => response.json());
        },
        [b2cRequest, currentCulture]
    );

    const fetchAuthBasket = useCallback(async (): Promise<DA_BasketDataApi> => {
        try {
            if (globalThis?.b2bsitevariant === "hatting-agro-b2b" && customerNumber) {
                const response = await b2cRequest
                    .get(`/basket/customer/${customerNumber}`)
                    .then((response) => response.json());
                return response;
            } else if (siteVariant.alias === "hatting-agro-b2b" && customerNumber) {
                const response = await b2cRequest
                    .get(`api/basket/customer/${customerNumber}`)
                    .then((response) => response.json());
                return response;
            } else {
                const response = await b2cRequest
                    .get("/api/basket/")
                    .then((response) => response.json());
                return response;
            }
        } catch (error) {
            console.error("Error fetching auth basket:", error);
            // Fallback to regular basket endpoint if customer-specific endpoint fails
            const response = await b2cRequest
                .get("/api/basket/")
                .then((response) => response.json());
            return response;
        }
        // .then((data) => new Promise((resolve) => setTimeout(() => resolve(data), 5000)));
    }, [b2cRequest, siteVariant.alias, customerNumber]);

    const updateBasketData = useCallback(
        (basketData: DA_BasketData) => {
            if (basketData) {
                setBasketId(basketData.id);
                setBasketSecret(basketData.secret);
                // Only store in localStorage for B2C sites
                if (!isB2BSite && !loggedIn) {
                    localStorage.setItem("basketId", basketData.id);
                    localStorage.setItem("basketSecret", basketData.secret);
                }

                setOrderLines((prevLines) => {
                    if (prevLines.length === 0 && basketData.orderLines.length > 0) {
                        setIsFirstAdd(true);
                    } else {
                        setIsFirstAdd(false);
                    }
                    return basketData.orderLines;
                });

                setPriceData(basketData.prices);
                setShippingDetails(basketData.shippingDetails);
                setDeliveryMethod(basketData.deliveryMethod);
            }
        },
        [isB2BSite, loggedIn]
    );

    const addToBasket: BasketHook["addToBasket"] = useCallback(
        async (properties, options) => {
            const cartLine = mapToBasketLineApi(properties);

            setIsUpdatingBasket(true);

            try {
                // For B2B sites (Hatting), only handle authenticated users
                if (isB2BSite) {
                    if (!loggedIn) {
                        throw new Error("Authentication required for B2B basket operations");
                    }
                }

                // For B2C sites, handle both authenticated and anonymous users
                if (loggedIn) {
                    let response;
                    if (siteVariant.alias === "hatting-agro-b2b") {
                        response = await b2cRequest.post(
                            `/api/basket/customers/${customerNumber}/lines`,
                            null,
                            cartLine,
                            options
                        );
                    } else {
                        response = await b2cRequest.post(
                            `/api/basket/lines/`,
                            null,
                            cartLine,
                            options
                        );
                    }
                    const data = await response.json();
                    if (data && data.id) {
                        updateBasketData(mapToBasketData(data));
                        const updatedLine = findUpdatedLine(data.orderLines, properties);

                        // Prefer response data, then properties, then translation fallback
                        const productName =
                            updatedLine?.productName ||
                            properties.productName ||
                            getDictionaryString("ToastUnknownProduct");

                        if (options?.showToast) {
                            showBasketToast({ productName, getDictionaryString });
                        }

                        updatingCartMessage({ method: "add" });
                        return data;
                    } else {
                        throw new Error("Failed to add item to basket");
                    }
                } else {
                    let localBasketId = basketId;
                    let localBasketSecret = basketSecret;

                    // Create a new basket if no basket exists
                    if (!basketId || !basketSecret || isBasketMerged.current) {
                        const newBasket = await b2cRequest
                            .get("/api/basket/new/")
                            .then((response) => response.json());

                        // TODO: Verify camelCase on newBasket
                        localBasketId = newBasket.id;
                        localBasketSecret = newBasket.secret;

                        // Store the new basket details
                        storeBasketId(localBasketId);
                        storeBasketSecret(localBasketSecret);
                    }

                    // Add the item to the basket
                    if (localBasketId && localBasketSecret) {
                        const response = await b2cRequest.post(
                            `/api/basket/${localBasketId}/lines/`,
                            localBasketSecret,
                            cartLine,
                            options
                        );
                        const data = await response.json();

                        if (data && data.id) {
                            updateBasketData(mapToBasketData(data));
                            const updatedLine = findUpdatedLine(data.orderLines, properties);

                            // Prefer response data, then properties, then translation fallback
                            const productName =
                                updatedLine?.productName ||
                                properties.productName ||
                                getDictionaryString("ToastUnknownProduct");

                            if (options?.showToast) {
                                showBasketToast({ productName, getDictionaryString });
                            }

                            updatingCartMessage({ method: "add" });
                            return data;
                        } else {
                            throw new Error("Failed to add item to basket");
                        }
                    }
                }
            } catch (error) {
                console.error("Error adding to basket:", error);
                throw error; // Re-throw the error to allow the caller to handle it
            } finally {
                setIsUpdatingBasket(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            b2cRequest,
            basketId,
            basketSecret,
            loggedIn,
            storeBasketId,
            storeBasketSecret,
            updateBasketData,
            updatingCartMessage,
            isB2BSite,
        ]
    );

    const resetBasket: BasketHook["resetBasket"] = useCallback(() => {
        // Only clear localStorage for B2C sites
        if (!isB2BSite) {
            localStorage.removeItem("basketId");
            localStorage.removeItem("basketSecret");
        }
        setBasketId(undefined);
        setBasketSecret(undefined);
        setOrderLines([]);
        setIsLoadingBasket(false);
    }, [isB2BSite]);

    const patchDeliveryMethod: BasketHook["patchDeliveryMethod"] = useCallback(
        async (deliveryMethod, options) => {
            setIsUpdatingBasket(true);

            if (isB2BSite) {
                const opts = {
                    ...options,
                    method: "PATCH",
                    headers: {
                        "x-basket-secret": basketSecret,
                    },
                };

                if (globalThis.isAuthenticated) {
                    opts.headers["Authorization"] = `Bearer ${globalThis.accessToken}`;
                }

                const params = new URLSearchParams({
                    culture: currentCulture,
                });

                const path = `/api/Basket/${basketId}/customer/${customerNumber}/delivery-method?${params.toString()}`;
                const response = await request.patch(path, JSON.stringify(deliveryMethod), opts);
                const data = await response.json();

                if (data) {
                    updateBasketData(mapToBasketData(data));
                }
            }
        },
        [
            isB2BSite,
            basketSecret,
            currentCulture,
            basketId,
            customerNumber,
            request,
            updateBasketData,
        ]
    );

    const updateDeliveryMethod: BasketHook["updateDeliveryMethod"] = useCallback(
        async (carrier, carrier_service, options) => {
            setIsUpdatingBasket(true);

            try {
                const homerunnerData = {
                    homerunnerCarrier: carrier,
                    homerunnerService: carrier_service,
                };

                let response;
                if (loggedIn) {
                    response = await b2cRequest.patch(
                        `/api/basket/homerunner-data`,
                        null,
                        homerunnerData,
                        options
                    );
                } else {
                    response = await b2cRequest.patch(
                        `/api/basket/${basketId}/homerunner-data`,
                        basketSecret,
                        homerunnerData,
                        options
                    );
                }

                const data = await response.json();

                if (data && data.id) {
                    updateBasketData(mapToBasketData(data));
                    return data;
                } else {
                    throw new Error("Failed to update delivery method");
                }
            } catch (error) {
                console.error("Error updating delivery method:", error);
                throw error;
            } finally {
                setIsUpdatingBasket(false);
            }
        },
        [b2cRequest, basketId, basketSecret, loggedIn, updateBasketData]
    );

    const updateBasket: BasketHook["updateBasket"] = useCallback(
        async (properties, options) => {
            setIsUpdatingBasket(true);

            try {
                // For B2B sites (Hatting), only handle authenticated users
                if (isB2BSite) {
                    if (!loggedIn) {
                        throw new Error("Authentication required for B2B basket operations");
                    }
                }

                // For B2C sites, handle both authenticated and anonymous users

                let updateUrl;
                if (loggedIn) {
                    if (siteVariant.alias === "hatting-agro-b2b") {
                        updateUrl = `/api/basket/customers/${customerNumber}/lines/${properties["id"]}/updateQuantity`;
                    } else {
                        updateUrl = `/api/basket/lines/${properties["id"]}/updatequantity`;
                    }
                } else {
                    updateUrl = `/api/basket/${basketId}/lines/${properties["id"]}/updatequantity`;
                }

                const response = await b2cRequest.patch(
                    updateUrl,
                    loggedIn ? null : basketSecret,
                    properties.quantity?.toString(),
                    options
                );

                const data = await response.json();

                if (data) {
                    // Update the basket data in the state
                    updatingCartMessage({ method: "update" });
                    updateBasketData(mapToBasketData(data));
                    return data;
                } else {
                    throw new Error("Failed to update basket");
                }
            } catch (error) {
                console.error("Error updating basket:", error);
                throw error; // Re-throw the error to allow the caller to handle it
            } finally {
                setIsUpdatingBasket(false);
            }
        },
        [
            b2cRequest,
            basketId,
            basketSecret,
            loggedIn,
            updateBasketData,
            updatingCartMessage,
            isB2BSite,
            customerNumber,
            siteVariant.alias,
        ]
    );

    const removeFromBasket: BasketHook["removeFromBasket"] = useCallback(
        async (properties, options) => {
            setIsUpdatingBasket(true);

            try {
                const lineId = properties["id"];

                // For B2B sites (Hatting), only handle authenticated users
                if (isB2BSite) {
                    if (!loggedIn) {
                        throw new Error("Authentication required for B2B basket operations");
                    }
                }

                // For B2C sites, handle both authenticated and anonymous users

                let deleteUrl;
                if (loggedIn) {
                    if (siteVariant.alias === "hatting-agro-b2b") {
                        deleteUrl = `/api/basket/customers/${customerNumber}/lines/${lineId}`;
                    } else {
                        deleteUrl = `/api/basket/lines/${lineId}`;
                    }
                } else {
                    deleteUrl = `/api/basket/${basketId}/lines/${lineId}`;
                }

                // Make the API call to remove the item from the basket
                const response = await b2cRequest.delete(
                    deleteUrl,
                    loggedIn ? null : basketSecret,
                    {},
                    options
                );

                const data = await response.json();
                if (data && data.id) {
                    // Update the basket data in the state
                    updateBasketData(mapToBasketData(data));
                    updatingCartMessage({ method: "delete" });
                    return data;
                } else {
                    throw new Error("Failed to remove item from basket");
                }
            } catch (error) {
                console.error("Error removing item from basket:", error);
                throw error; // Re-throw the error to allow the caller to handle it
            } finally {
                setIsUpdatingBasket(false);
            }
        },
        [
            b2cRequest,
            basketId,
            basketSecret,
            loggedIn,
            updateBasketData,
            updatingCartMessage,
            isB2BSite,
            customerNumber,
            siteVariant.alias,
        ]
    );

    const getCompletedOrder: BasketHook["getCompletedOrder"] = useCallback(
        async (orderId, orderSecret) => {
            setIsUpdatingBasket(true);

            try {
                const response = await fetchAnonymousBasket(orderId, orderSecret);
                return mapToOrder(response);
            } catch (error) {
                console.error("Error fetching completed order:", error);
                throw error; // Re-throw the error to allow the caller to handle it
            } finally {
                setIsUpdatingBasket(false);
            }
        },
        [fetchAnonymousBasket]
    );

    const ensureBasket = useCallback(() => {
        // For B2B sites (Hatting), only fetch if logged in (no localStorage checking)
        if (isB2BSite) {
            if (loggedIn) {
                setIsLoadingBasket(true);
                fetchAuthBasket()
                    .then((basket) => {
                        updateBasketData(mapToBasketData(basket));
                        setIsLoadingBasket(false);
                    })
                    .catch((error) => {
                        console.error("Error fetching auth basket:", error);
                        setIsLoadingBasket(false);
                    });
            }
            return;
        }

        // For B2C sites, check localStorage for anonymous baskets
        const localBasketId = localStorage.getItem("basketId");
        const localBasketSecret = localStorage.getItem("basketSecret");
        const hasLocalBasket = !!localBasketId && !!localBasketSecret;

        // User is logged in and we have a local basket that needs merging
        if (loggedIn && hasLocalBasket && !isBasketMerged.current) {
            // Delete local state before merging
            resetBasket();

            mergeAnonymousBasket(localBasketId, localBasketSecret).then((basket) => {
                // Update local state with merged basket
                updateBasketData(mapToBasketData(basket));
                isBasketMerged.current = true;
                localStorage.removeItem("basketId");
                localStorage.removeItem("basketSecret");
            });

            return;
        }

        // If no local basket, just fetch the authenticated basket
        if (loggedIn || (localBasketId && localBasketSecret)) {
            setIsLoadingBasket(true);

            if (loggedIn) {
                fetchAuthBasket()
                    .then((basket) => {
                        updateBasketData(mapToBasketData(basket));
                        setIsLoadingBasket(false);
                    })
                    .catch((error) => {
                        console.error("Error fetching auth basket:", error);
                        setIsLoadingBasket(false);
                    });
            } else {
                fetchAnonymousBasket(localBasketId, localBasketSecret)
                    .then((basket) => {
                        updateBasketData(mapToBasketData(basket));
                        setIsLoadingBasket(false);
                    })
                    .catch((error) => {
                        console.error("Error fetching anonymous basket:", error);
                        setIsLoadingBasket(false);
                    });
            }
        }
    }, [
        isB2BSite,
        loggedIn,
        fetchAuthBasket,
        updateBasketData,
        resetBasket,
        mergeAnonymousBasket,
        fetchAnonymousBasket,
    ]);

    useEffect(() => {
        // Only merge baskets for B2C sites
        if (isB2BSite) {
            return;
        }

        const hasLocalBasket =
            !!localStorage.getItem("basketId") && !!localStorage.getItem("basketSecret");

        if (loggedIn && hasLocalBasket && !isBasketMerged.current) {
            const localBasketId = localStorage.getItem("basketId");
            const localsecret = localStorage.getItem("basketSecret");

            // Delete local state before merging
            resetBasket();

            mergeAnonymousBasket(localBasketId, localsecret)
                .then((basket) => {
                    // Update local state with merged basket
                    updateBasketData(mapToBasketData(basket));
                    isBasketMerged.current = true;
                    localStorage.removeItem("basketId");
                    localStorage.removeItem("basketSecret");
                })
                .catch((error) => {
                    console.error("Error merging anonymous basket:", error);
                    // If merge fails, still try to fetch the auth basket
                    fetchAuthBasket()
                        .then((basket) => {
                            updateBasketData(mapToBasketData(basket));
                        })
                        .catch((authError) => {
                            console.error(
                                "Error fetching auth basket after merge failure:",
                                authError
                            );
                        });
                });
        }
    }, [loggedIn, mergeAnonymousBasket, resetBasket, updateBasketData, isB2BSite, fetchAuthBasket]);

    useEffect(() => {
        // Entry point: Ensure the basket is loaded when the component mounts
        ensureBasket();
    }, [ensureBasket]);

    return (
        <BasketContext.Provider
            value={{
                basketId,
                secret: basketSecret,
                orderSecret,
                orderLines: orderLines,
                shippingDetails,
                deliveryMethod,
                resetBasket,
                addToBasket,
                updateBasket,
                removeFromBasket,
                updateDeliveryMethod,
                patchDeliveryMethod,
                getCompletedOrder,
                isLoadingBasket,
                isUpdatingBasket,
                priceData,
                isFirstAdd,
                messageOnUpdatingCart,
                updatingCartMessage,
            }}
        >
            {children}
        </BasketContext.Provider>
    );
};

export const useBasket = (): BasketHook => React.useContext(BasketContext);
